import { useState, useEffect } from 'react';
import axios from 'axios';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, TrendingDown, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Budget = ({ token }) => {
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBudgetStatus();
  }, []);

  const fetchBudgetStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stats/budget`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBudgetData(response.data);
    } catch (error) {
      console.error('Error fetching budget status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-body text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const categoriesWithBudget = budgetData.filter(cat => cat.budget_amount > 0);

  return (
    <div className="space-y-6" data-testid="budget-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
          Budget Tracking
        </h1>
        <p className="mt-1 text-base font-body text-muted-foreground">
          Monitor your spending against category budgets
        </p>
      </div>

      {categoriesWithBudget.length === 0 ? (
        <div className="bg-card border border-border rounded-sm shadow-sm p-12 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-heading font-medium text-foreground mb-2">
            No Budgets Set
          </h3>
          <p className="font-body text-muted-foreground">
            Set budget amounts for your expense categories to start tracking.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categoriesWithBudget.map((category) => {
            const isOverBudget = category.spent_amount > category.budget_amount;
            const isNearLimit = category.percentage >= 80 && category.percentage < 100;

            return (
              <div
                key={category.category_id}
                className={`bg-card border rounded-sm shadow-sm p-6 ${
                  isOverBudget ? 'border-red-300' : 'border-border'
                }`}
                data-testid="budget-category-card"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground">
                      {category.category_name}
                    </h3>
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                      {isOverBudget ? 'Over Budget' : isNearLimit ? 'Near Limit' : 'On Track'}
                    </p>
                  </div>
                  <div>
                    {isOverBudget ? (
                      <div className="h-10 w-10 rounded-sm bg-red-50 flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      </div>
                    ) : isNearLimit ? (
                      <div className="h-10 w-10 rounded-sm bg-yellow-50 flex items-center justify-center">
                        <TrendingDown className="h-5 w-5 text-yellow-600" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-sm bg-green-50 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-body text-muted-foreground">Spent</span>
                    <span className="text-xl font-mono font-medium text-foreground">
                      ${category.spent_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-body text-muted-foreground">Budget</span>
                    <span className="text-xl font-mono font-medium text-foreground">
                      ${category.budget_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <Progress
                  value={Math.min(category.percentage, 100)}
                  className="h-3 mb-2"
                  data-testid="budget-progress"
                />

                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono text-muted-foreground">
                    {category.percentage.toFixed(1)}% used
                  </span>
                  <span
                    className={`font-mono font-medium ${
                      category.remaining >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {category.remaining >= 0 ? '$' : '-$'}
                    {Math.abs(category.remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {budgetData.length > 0 && (
        <div className="bg-card border border-border rounded-sm shadow-sm p-6">
          <h2 className="text-2xl font-heading font-medium mb-4">All Categories</h2>
          <div className="space-y-2">
            {budgetData.map((category) => (
              <div
                key={category.category_id}
                className="flex items-center justify-between p-4 border border-border rounded-sm"
                data-testid="budget-summary-row"
              >
                <div className="flex-1">
                  <span className="font-body font-medium text-foreground">
                    {category.category_name}
                  </span>
                  {category.budget_amount === 0 && (
                    <span className="ml-2 text-xs font-mono text-muted-foreground">
                      (No budget set)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-mono text-foreground">
                      ${category.spent_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground">Spent</p>
                  </div>
                  {category.budget_amount > 0 && (
                    <div className="text-right">
                      <p className="text-sm font-mono text-foreground">
                        ${category.budget_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground">Budget</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
